import type { SyntheticEvent } from 'react';
import React, { useCallback } from 'react';
import { Card, NotInStockVehicleActions, NotInStockVehicleDataContainer } from './BuildVehicleSearchResults.style';
import { Button } from '@interstate/components/Button';
import { Typography } from '@interstate/components/Typography';
import { useDispatch, useSelector } from 'react-redux';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';

import { changeVehicleCreators, changeVehicleSelectors, middlewareMgr, AI_BUILD_VEHICLE_SELECTED } from '../../store';
import type { CatalogVehicle } from '../../types';

import { NotInStockVehicleData } from './NotInStockVehicleData';
import { useAddNewScenarioAvailability } from '../changeVehicle/useAddNewScenarioAvailability';
import { useVehicleTitle, useValidateVehicle } from '../../utils';

import { createBuildVehicleSelectedEvent } from '../../analytics';
import { dealerSelectors } from '@makemydeal/dr-shared-store';

type NotInStockVehicleProps = {
    vehicle: CatalogVehicle;
    selectValues: {
        inventoryType: string;
        year: string;
        make: string;
        model: string;
        trim: string;
    };
    index: number;
};

const NotInStockVehicle: React.FC<NotInStockVehicleProps> = ({ vehicle, selectValues, index }) => {
    const dispatch = useDispatch();
    const selectedVehicle = useSelector(changeVehicleSelectors.getSelectedVehicle);
    const theme = useInterstateTheme();
    const isDealerAISearchEnabled = useSelector(dealerSelectors.isDealerAISearchEnabled);
    const agentPromptString = useSelector(changeVehicleSelectors.getAgentPromptString);

    // The built vehicle only becomes applicable once its price and odometer are filled in below, so the button reads the
    // same merged vehicle the footer would apply.
    const selectedVehicleWithCustomData = useSelector(changeVehicleSelectors.getSelectedVehicleWithCustomData);
    const isVehicleValid = useValidateVehicle(selectedVehicleWithCustomData);
    const isChangeProcessing = useSelector(changeVehicleSelectors.isChangeProcessing);

    // Same availability the row and the footer read, so none of the three can disagree about whether this host offers
    // scenarios or how many are left.
    const { canAddNewScenario, isScenarioLimitReached } = useAddNewScenarioAvailability();
    const isAddNewScenarioDisabled = !isVehicleValid || isScenarioLimitReached;

    const vehicleCardTitle = useVehicleTitle(vehicle);

    const isSelected = vehicle === selectedVehicle;

    // The footer's own apply button is hidden while inline actions are on, so a built vehicle needs its apply here.
    const handleSelectVehicleClick = (event: SyntheticEvent) => {
        event.stopPropagation();
        dispatch(changeVehicleCreators.setApplyVehicleRequested(true));
    };

    // A built vehicle is a Manager View concern like any other, so it gets the same scenario action the rows do.
    const handleAddNewScenarioClick = (event: SyntheticEvent) => {
        event.stopPropagation();
        dispatch(changeVehicleCreators.setAddNewScenarioRequested(true));
    };

    const selectVehicle = useCallback(() => {
        if (vehicle !== selectedVehicle) {
            // Dispatch vs-019 for build vehicle selection (AI and non-AI mode)
            const resultsRank = index + 1;
            const buildSelectedEvent = createBuildVehicleSelectedEvent(vehicle, resultsRank, agentPromptString || '');
            const hostAction = middlewareMgr.hostActions.forwardCDLEvent(buildSelectedEvent.payload);
            dispatch(hostAction);

            // Dispatch AI build vehicle selected action for SV search context forwarding
            if (isDealerAISearchEnabled) {
                dispatch({
                    type: AI_BUILD_VEHICLE_SELECTED,
                    payload: { vehicle, resultsRank, searchTerm: agentPromptString }
                });
            }

            dispatch(changeVehicleCreators.newVehicleSelected(vehicle));
        }
    }, [dispatch, vehicle, selectedVehicle, selectValues, index, agentPromptString, isDealerAISearchEnabled]);

    return (
        <Card className={isSelected ? 'active' : ''} onClick={selectVehicle} data-testid={`buildVehicle-${index}`} theme={theme}>
            <Typography variant="h5" tag="div" sx={{ mb: 2 }}>
                {selectValues.inventoryType} {vehicleCardTitle}
            </Typography>
            <div>
                <Typography variant="label-md" color="base.color.gray.600" sx={{ whiteSpace: 'nowrap', paddingRight: '5px' }}>
                    Engine Description:
                </Typography>
                {vehicle.engine}
            </div>
            <div>
                <Typography variant="label-md" color="base.color.gray.600" sx={{ whiteSpace: 'nowrap', paddingRight: '5px' }}>
                    Drivetrain Description:
                </Typography>
                {vehicle.driveLine}
            </div>
            <div>
                <Typography variant="label-md" color="base.color.gray.600" sx={{ whiteSpace: 'nowrap', paddingRight: '5px' }}>
                    Transmission:
                </Typography>
                {vehicle.transmission}
            </div>
            {isSelected && (
                <NotInStockVehicleDataContainer>
                    <NotInStockVehicleData />
                    <NotInStockVehicleActions>
                        <Button
                            buttonStyle="primary"
                            data-testid={`btn-select-vehicle-build-${index}`}
                            disabled={!isVehicleValid}
                            isLoading={isChangeProcessing}
                            onClick={handleSelectVehicleClick}
                        >
                            Select Vehicle
                        </Button>
                        {/* Only hosts that can create a scenario offer one, which today is Manager View alone. */}
                        {canAddNewScenario && (
                            <Button
                                buttonStyle="secondary"
                                data-testid={`btn-new-scenario-build-${index}`}
                                disabled={isChangeProcessing || isAddNewScenarioDisabled}
                                onClick={handleAddNewScenarioClick}
                            >
                                New Scenario
                            </Button>
                        )}
                    </NotInStockVehicleActions>
                </NotInStockVehicleDataContainer>
            )}
        </Card>
    );
};

export default NotInStockVehicle;
